import React, { Component } from 'react';
import { cloneDeep, isEmpty } from 'lodash';
import { withTranslation } from 'react-i18next';
import CargomaticCheckbox from 'components/v2/forms/Checkbox';
import ConfirmModal from 'components/ui/modals/ConfirmModal';
import UserWebAPIUtils from 'utils/UserWebAPIUtils';
import { titleize } from 'underscore.string';
import Spinner from 'components/ui/Spinner';
import SessionStore from '../../../stores/SessionStore';
import defaultNotificationTypesMap from '../../../config/notification-defaults';
import notificationTypes from '../../../config/notification-types';
import NotificationGroup from './notification-group';
import NotificationType from './notification-type';
import UserActionCreators from '../../../actions/UserActionCreators';
import ToastStore from '../../../stores/ToastStore';
import Alert from '../../ui/Alert';
import { getCustomerV2 } from '../../../utils/CustomersAPIUtils';
const hasOwnProperty = Object.prototype.hasOwnProperty;
const emailRegex = /^\w+([\.+-]?\w+)*@\w+([\.+-]?\w+)*(\.\w{2,})+$/; //eslint-disable-line
const phoneRegex = /^\+?\d?\s?\(?([0-9]{3})\)?[-.\s ]?([0-9]{3})[-.\s ]?([0-9]{4})$/; //eslint-disable-line
function transformNotification(notificationGroup, notificationType, transport) {
    const value = notificationType[transport].value || [];
    let recipients;
    if (!value) {
        recipients = [];
    }
    else {
        recipients = value.map((r) => r.trim());
    }
    return {
        category: notificationGroup,
        event: notificationType.type,
        transport,
        silent: notificationType[transport].silent,
        recipients,
    };
}
class NotificationsManager extends Component {
    constructor(props) {
        super(props);
        this.handleAdminPermissionChange = async (value) => {
            const { t } = this.props;
            try {
                if (value) {
                    const user = SessionStore.getUser();
                    const result = await UserWebAPIUtils.createGrants(user._id, '*', 'loginAs', 'role-based', user._type.toLowerCase());
                    if (result.success) {
                        this.setState({ enableLoginAs: result.success });
                    }
                }
                else {
                    this.setState({
                        showPermissionConfirmationModal: true,
                        message: t('ABOUT_TO_REVOKE_PERMISSION_FOR_LOGIN_TIP'),
                    });
                }
            }
            catch (e) {
                console.log('Error granting request', e.message);
            }
        };
        this.handleConfirmPermissionRevoke = async () => {
            try {
                const user = SessionStore.getUser();
                const result = await UserWebAPIUtils.revokeAdminPermission(user._id, '*', 'loginAs', 'role-based', user._type.toLowerCase(), new Date().toISOString());
                if (result.success) {
                    this.setState({
                        enableLoginAs: false,
                        showPermissionConfirmationModal: false,
                    });
                }
            }
            catch (e) {
                console.log('Error creating revoke request', e.message);
            }
        };
        this.handleNotification = () => {
            const { t } = this.props;
            const notification = ToastStore.getLatestNotification();
            if (!notification) {
                return;
            }
            if (notification.level === 'success') {
                const successMessage = notification.body || notification.title;
                this.setState({ successMessage });
            }
            else {
                const errors = { status: t('PROBLEM_CREATING_YOUR_ACCOUNT_TIP') };
                this.setState({ errors, successMessage: '' });
            }
        };
        this.mapNotificationPreferences = async () => {
            let user;
            let notificationPreferences;
            if (this.props.userNotificationPreferences) {
                user = this.props.userNotificationPreferences;
                notificationPreferences = user.notification_preferences;
            }
            else {
                user = await getCustomerV2(this.getUserId());
                if (!user) {
                    return;
                }
                notificationPreferences = user.notification_preferences;
            }
            const currentNotificationsMap = notificationPreferences.reduce((map, pref) => {
                const mapCopy = Object.assign({}, map);
                const type = pref.type;
                const mappedPref = {
                    type: pref.type,
                    _id: pref._id,
                    text: {
                        silent: pref.text.silent,
                        value: pref.text.recipients,
                    },
                    email: {
                        silent: pref.email.silent,
                        value: pref.email.recipients,
                    },
                };
                mapCopy[type] = mappedPref;
                return mapCopy;
            }, {});
            const userNotificationsMap = defaultNotificationTypesMap[user._type.toLowerCase()].reduce((map, pref) => {
                var _a, _b;
                const mapCopy = Object.assign({}, map);
                const type = pref.type;
                if (hasOwnProperty.call(currentNotificationsMap, type)) {
                    mapCopy[type] = currentNotificationsMap[type];
                    mapCopy[type].text.hidden = ((_a = pref === null || pref === void 0 ? void 0 : pref.text) === null || _a === void 0 ? void 0 : _a.hidden) || false;
                    mapCopy[type].email.hidden = ((_b = pref === null || pref === void 0 ? void 0 : pref.email) === null || _b === void 0 ? void 0 : _b.hidden) || false;
                }
                else {
                    const mappedPref = {
                        type: pref.type,
                        _id: pref._id,
                        text: {
                            silent: pref.text.silent,
                            value: pref.text.recipients,
                            hidden: pref.text.hidden === undefined ? false : pref.text.hidden,
                        },
                        email: {
                            silent: pref.email.silent,
                            value: pref.email.recipients,
                            hidden: pref.text.hidden === undefined ? false : pref.email.hidden,
                        },
                    };
                    mapCopy[type] = mappedPref;
                }
                return mapCopy;
            }, {});
            const notificationGroups = notificationTypes
                .filter((notificationType) => hasOwnProperty.call(userNotificationsMap, notificationType.type))
                .filter((notificationType) => NOTIFICATION_TYPES.includes(notificationType.type))
                .reduce((group, notificationType) => {
                const category = notificationType.category;
                const mappedGroup = Object.assign({}, group);
                if (!hasOwnProperty.call(group, category)) {
                    mappedGroup[category] = {
                        notificationTypes: [],
                    };
                }
                const type = notificationType.type;
                const mappedNotificationType = Object.assign(Object.assign({}, notificationType), {
                    email: userNotificationsMap[type].email,
                    text: userNotificationsMap[type].text,
                    category,
                });
                const hidden = mappedNotificationType.email.hidden &&
                    mappedNotificationType.text.hidden;
                if (!hidden) {
                    mappedGroup[category].notificationTypes.push(mappedNotificationType);
                }
                return mappedGroup;
            }, {});
            this.setState({ notificationGroups, user });
        };
        this.state = {
            notificationGroups: {},
            errors: {},
            successMessage: '',
            enableLoginAs: false,
            showPermissionConfirmationModal: false,
            message: '',
        };
    }
    async componentDidMount() {
        if (this.props.userNotificationPreferences == null) {
            SessionStore.addChangeListener(this.mapNotificationPreferences);
            ToastStore.addChangeListener(this.handleNotification);
        }
        this.mapNotificationPreferences();
        await this.getLoginContext();
    }
    componentWillUnmount() {
        if (this.props.userNotificationPreferences == null) {
            SessionStore.removeChangeListener(this.mapNotificationPreferences);
            ToastStore.removeChangeListener(this.handleNotification);
        }
    }
    async getLoginContext() {
        const scopes = await UserWebAPIUtils.getUserContextGrants(['login']);
        if (scopes && scopes.login) {
            this.setState({
                enableLoginAs: true,
            });
        }
    }
    getTargetNotificationIndex(notificationGroup, notificationType) {
        return this.state.notificationGroups[notificationGroup].notificationTypes.findIndex((n) => n.type === notificationType.type);
    }
    getTargetNotification(notificationGroup, index) {
        return this.state.notificationGroups[notificationGroup].notificationTypes[index];
    }
    getUserId() {
        if (this.props.userNotificationPreferences != null) {
            return this.props.userNotificationPreferences._id;
        }
        return SessionStore.getUser()._id;
    }
    getHandleNotificationChange(notificationGroup, notificationType, transport) {
        return (checked) => {
            const index = this.getTargetNotificationIndex(notificationGroup, notificationType);
            if (index !== -1) {
                const copy = this.applyNotificationChange(notificationGroup, index, transport, !checked);
                this.setState({
                    notificationGroups: copy,
                });
                UserActionCreators.updateNotificationEvent(this.getUserId(), transformNotification(notificationGroup, copy[notificationGroup].notificationTypes[index], transport));
            }
        };
    }
    getHandleRecipientsChange(notificationGroup, notificationType, transport) {
        return (value) => {
            const index = this.getTargetNotificationIndex(notificationGroup, notificationType);
            if (index !== -1) {
                const copy = this.applyRecipientsTextChange(notificationGroup, index, transport, value);
                this.setState({
                    notificationGroups: copy,
                });
            }
        };
    }
    getHandleRecipientsBlur(notificationGroup, notificationType, transport) {
        return () => {
            const index = this.getTargetNotificationIndex(notificationGroup, notificationType);
            if (index !== -1) {
                const targetNotification = this.getTargetNotification(notificationGroup, index);
                if (this.validateRecipients(targetNotification, transport)) {
                    UserActionCreators.updateNotificationRecipients(this.getUserId(), transformNotification(notificationGroup, targetNotification, transport));
                }
            }
        };
    }
    validateRecipients(notificationType, transport) {
        const value = notificationType[transport].value || [];
        if (!value) {
            return true;
        }
        const recipients = value.map((r) => r.trim());
        let valid = true;
        if (transport === 'email') {
            recipients.forEach((r) => {
                if (!r.match(emailRegex)) {
                    valid = false;
                }
            });
        }
        else if (transport === 'text') {
            recipients.forEach((r) => {
                if (!r.match(phoneRegex)) {
                    valid = false;
                }
            });
        }
        return valid;
    }
    applyNotificationChange(notificationGroup, index, transport, silent) {
        const targetNotification = this.getTargetNotification(notificationGroup, index);
        const updatedNotfication = Object.assign(Object.assign({}, targetNotification), {
            [transport]: Object.assign(Object.assign({}, targetNotification[transport]), {
                silent,
            }),
        });
        const updated = cloneDeep(this.state.notificationGroups);
        updated[notificationGroup].notificationTypes[index] = updatedNotfication;
        return updated;
    }
    applyRecipientsTextChange(notificationGroup, index, transport, value) {
        const targetNotification = this.getTargetNotification(notificationGroup, index);
        const updatedNotfication = Object.assign(Object.assign({}, targetNotification), {
            [transport]: Object.assign(Object.assign({}, targetNotification[transport]), {
                value,
            }),
        });
        const updated = cloneDeep(this.state.notificationGroups);
        updated[notificationGroup].notificationTypes[index] = updatedNotfication;
        return updated;
    }
    renderSuccessMessage() {
        if (this.state.successMessage) {
            return React.createElement(Alert, { type: "success" }, this.state.successMessage);
        }
        return null;
    }
    renderErrors() {
        const errors = this.state.errors;
        const errorKeys = Object.keys(errors);
        if (errorKeys.length > 0) {
            return (React.createElement("div", { className: "mb-4" },
                React.createElement(Alert, { type: "error" },
                    React.createElement("ul", null, errorKeys.map((errorKey, i) => (React.createElement("li", { key: i }, errors[errorKey])))))));
        }
        return null;
    }
    isNotificationsGroupAllowedToRender(category) {
        const { user: { _type, tags }, } = this.state;
        // don't render notification group if carrier is not third party for appointment
        if (_type === 'Carrier' &&
            !tags.includes('third_party_carrier') &&
            category === 'appointment') {
            return false;
        }
        return true;
    }
    render() {
        const { t } = this.props;
        return (React.createElement("div", { className: "notifications-manager" },
            this.renderErrors(),
            this.renderSuccessMessage(),
            SessionStore.getUser()._type !== 'Admin' && (React.createElement(React.Fragment, null,
                React.createElement("div", { className: "permissionBlock mb-4" },
                    React.createElement("h3", { className: "my-0 mb-4 text-lg" }, t('PERMISSION')),
                    React.createElement(CargomaticCheckbox, { checked: this.state.enableLoginAs, onChange: (value) => this.handleAdminPermissionChange(value) }, t('GRANT_PERMISSION_TO_ADMINISTRATOR'))),
                React.createElement(ConfirmModal, { show: this.state.showPermissionConfirmationModal, title: t('REVOKE_PERMISSION'), message: this.state.message, onConfirm: this.handleConfirmPermissionRevoke, onHide: () => this.setState({ showPermissionConfirmationModal: false }), affirmative: t('REVOKE_ACCESS'), hideButtonText: t('CANCEL'), primaryButtonColor: "red" }))),
            React.createElement("h3", { className: "my-0 mb-4 text-lg" }, titleize(t('NOTIFICATION', { count: 2 }))),
            React.createElement("p", { className: "mb-4 text-sm" }, t('NOTIFICATION_EMAIL_AND_PHONE_NOTE')),
            React.createElement("div", { className: "" }, isEmpty(this.state.notificationGroups) ? (React.createElement("div", { className: "justify-center lg:col-span-1", style: { top: '50%' } },
                React.createElement("div", { className: "align-middle" },
                    React.createElement("span", { className: "text-5xl text-green" },
                        React.createElement(Spinner, { showMessage: false }))))) : (Object.keys(this.state.notificationGroups).map((category, i) => {
                return (this.isNotificationsGroupAllowedToRender(category) && (React.createElement(NotificationGroup, { key: i, category: category },
                    React.createElement("div", { className: "-mx-2" }, this.state.notificationGroups[category].notificationTypes.map((notificationType) => (React.createElement("div", { className: "px-2 mb-6", key: `${category}-${notificationType.type}` },
                        React.createElement(NotificationType, { notificationType: notificationType, onEmailRecipientsChange: this.getHandleRecipientsChange(category, notificationType, 'email'), onTextRecipientsChange: this.getHandleRecipientsChange(category, notificationType, 'text'), onEmailNotificationChange: this.getHandleNotificationChange(category, notificationType, 'email'), onTextNotificationChange: this.getHandleNotificationChange(category, notificationType, 'text'), onEmailRecipientsBlur: this.getHandleRecipientsBlur(category, notificationType, 'email'), onTextRecipientsBlur: this.getHandleRecipientsBlur(category, notificationType, 'text') }))))))));
            })))));
    }
}
export default withTranslation()(NotificationsManager);
